<template>
  <button
    type="button"
    class="ds-row"
    :class="{ 'ds-row--danger': danger }"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <span class="ds-row__label">
      <img
        v-if="pro"
        class="ds-row__pro"
        :src="proBadge"
        alt="PRO PLUS members only"
        width="35"
        height="15"
      />
      <span class="ds-row__text">
        <span class="ds-row__title"><slot>{{ label }}</slot></span>
        <span v-if="hint" class="ds-row__hint">{{ hint }}</span>
      </span>
    </span>
    <span class="ds-row__trailing">
      <span v-if="status" class="ds-row__status">{{ status }}</span>
      <span v-if="showToggle" class="ds-row__toggle" :class="{ 'ds-row__toggle--on': toggleOn }" aria-hidden="true">
        <span class="ds-row__knob"></span>
      </span>
      <span v-else-if="icon" class="material-icons ds-row__icon">{{ icon }}</span>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import proBadge from '../../../images/PRO-PLUS.png';

export default defineComponent({
  name: 'DetailSheetActionRow',
  props: {
    label: { type: String, default: '' },
    // Material Icons ligature name for the trailing affordance.
    icon: { type: String, default: '' },
    // Secondary line under the label, for things like a share URL.
    hint: { type: String, default: '' },
    // Transient trailing text, e.g. "Copied!".
    status: { type: String, default: '' },
    pro: { type: Boolean, default: false },
    danger: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // Renders a switch in place of the trailing icon, reflecting `toggleOn`.
    showToggle: { type: Boolean, default: false },
    toggleOn: { type: Boolean, default: false },
  },
  emits: ['click'],
  setup() {
    return { proBadge };
  },
});
</script>

<style scoped>
.ds-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.8125rem 0.875rem;
  background: transparent;
  border: 0;
  color: #fff;
  text-align: left;
  font-size: 0.9375rem;
  transition: background-color 0.15s ease;
}

/* Each row draws its own divider based on its position inside the section
   panel, so no <hr> soup is needed in the consuming templates. */
.ds-row:not(:first-child) {
  border-top: 1px solid var(--gray-border);
}

.ds-row:hover:not(:disabled),
.ds-row:focus-visible:not(:disabled) {
  background: var(--highlight);
}

.ds-row:disabled {
  opacity: 0.45;
}

.ds-row--danger,
.ds-row--danger .ds-row__icon {
  color: var(--red);
}

.ds-row__label {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ds-row__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.ds-row__title {
  line-height: 1.35;
}

.ds-row__hint {
  color: var(--text-muted);
  font-size: 0.75rem;
  line-height: 1.3;
  /* A hint is typically a share URL: one long token with no spaces to break at. It has to
     wrap inside the row — not widen the dialog, and not run under the trailing icon, which
     is what "share icon overlapping the share link" looks like on screen.
     Three rules, because each covers a different way that goes wrong:
       - `anywhere` is the modern break rule, with `break-word` declared first so engines
         that drop `anywhere` (Safari < 15.4 and older in-app webviews) still break the
         token instead of overflowing their box;
       - `min-width: 0` + `overflow: hidden` are the backstop: a token that nothing can
         break is clipped at the row's own edge rather than painted over the icon;
       - the trailing padding keeps a line that fills the full width from reading as if it
         touches the icon, without widening the gap every other (hint-less) row uses. */
  min-width: 0;
  overflow: hidden;
  overflow-wrap: break-word;
  overflow-wrap: anywhere;
  padding-right: 0.25rem;
}

.ds-row__pro {
  flex: 0 0 auto;
}

.ds-row__trailing {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ds-row__status {
  color: var(--yellow);
  font-size: 0.75rem;
  font-weight: 600;
}

.ds-row__icon {
  color: var(--text-muted);
  font-size: 20px;
}

.ds-row:hover:not(:disabled) .ds-row__icon {
  color: var(--yellow);
}

.ds-row--danger:hover:not(:disabled) .ds-row__icon {
  color: var(--red);
}

.ds-row__toggle {
  position: relative;
  display: block;
  width: 42px;
  height: 21px;
  border-radius: 10.5px;
  background: #363636;
  transition: background-color 0.15s ease;
}

.ds-row__toggle--on {
  background: var(--green);
}

.ds-row__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s ease;
}

.ds-row__toggle--on .ds-row__knob {
  transform: translateX(21px);
}
</style>
